import { useEffect, useRef, useState } from "react";
import {
  Button,
  Empty,
  Image,
  Input,
  Popconfirm,
  Popover,
  Select,
  Spin,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  PictureOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getClientNotes, toAddNote, toRemoveNote } from "../APIs";
import { errorMsg } from "../../../Utils/Utils";

const { Option } = Select;
const { TextArea } = Input;

const NOTE_TYPES = {
  success: { label: "Succès", color: "#52c41a" },
  danger: { label: "Danger", color: "#ff4d4f" },
  warning: { label: "Attention", color: "#faad14" },
};

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

const fileToDataUrl = (file, max = 1200, quality = 0.7) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = document.createElement("img");
      img.onload = () => {
        let { width, height } = img;
        if (width > max || height > max) {
          const ratio = Math.min(max / width, max / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => reject(new Error("Image invalide"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Lecture impossible"));
    reader.readAsDataURL(file);
  });

const ClientNotes = ({ clientId, canAdd = false, variant = "panel" }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [type, setType] = useState("warning");
  const [image, setImage] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadNotes = () => {
    if (null == clientId) {
      setNotes([]);
      return;
    }
    setLoading(true);
    getClientNotes(clientId)
      .then((res) => setNotes(res))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setMessage("");
    setImage(null);
    setImageName(null);
    loadNotes();
  }, [clientId]);

  const fileInputRef = useRef(null);

  const pickImage = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      errorMsg("Merci de choisir une image");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      errorMsg("L'image est trop volumineuse (10 Mo max)");
      return;
    }
    fileToDataUrl(file)
      .then((dataUrl) => {
        setImage(dataUrl);
        setImageName(file.name);
      })
      .catch(() => errorMsg("Impossible de lire l'image"));
    e.target.value = "";
  };

  const submitNote = () => {
    if (message.trim() === "") {
      errorMsg("Le message de la note est obligatoire");
      return;
    }
    setSaving(true);
    toAddNote(clientId, message.trim(), type, image)
      .then(() => {
        setMessage("");
        setImage(null);
        setImageName(null);
        loadNotes();
      })
      .catch(() => errorMsg("Une erreur est survenue"))
      .finally(() => setSaving(false));
  };

  const removeNote = (id) => {
    toRemoveNote(id).then(() => loadNotes());
  };

  const renderNote = (note) => {
    const conf = NOTE_TYPES[note.type] || NOTE_TYPES.warning;
    return (
      <div
        className={`client-notes__card client-notes__card--${note.type}`}
        key={note.id}
      >
        <div className="client-notes__meta">
          <Tag
            color={conf.color}
            className="client-notes__badge"
            style={{ borderColor: conf.color }}
          >
            {conf.label}
          </Tag>
          {note.createdBy && (
            <span className="client-notes__by">
              <UserOutlined /> {note.createdBy}
            </span>
          )}
          <span className="client-notes__date">
            {note.createdAt
              ? moment(note.createdAt).format("DD/MM/YYYY HH:mm")
              : "-"}
          </span>
          {canAdd && (
            <Popconfirm
              title="Supprimer cette note ?"
              okText="Oui"
              cancelText="Non"
              onConfirm={() => removeNote(note.id)}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </div>
        <p className="client-notes__body">{note.note}</p>
        {note.image && (
          <Image
            src={note.image}
            alt="note"
            width={140}
            className="client-notes__image"
          />
        )}
      </div>
    );
  };

  const renderTickerItem = (note) => {
    const conf = NOTE_TYPES[note.type] || NOTE_TYPES.warning;
    return (
      <span
        className={`client-notes__ticker-item client-notes__card--${note.type}`}
      >
        <span
          className="client-notes__ticker-type"
          style={{ backgroundColor: conf.color }}
        >
          {conf.label}
        </span>
        <span className="client-notes__ticker-text">{note.note}</span>
        {note.image && (
          <Popover
            trigger="hover"
            content={
              <img
                src={note.image}
                alt="note"
                style={{ maxWidth: 280, maxHeight: 280, display: "block" }}
              />
            }
          >
            <PictureOutlined className="client-notes__ticker-picture" />
          </Popover>
        )}
        {note.createdBy && (
          <span className="client-notes__ticker-by">— {note.createdBy}</span>
        )}
        {note.createdAt && (
          <span className="client-notes__ticker-date">
            {moment(note.createdAt).format("DD/MM/YYYY HH:mm")}
          </span>
        )}
      </span>
    );
  };

  if (variant === "ticker") {
    if (loading) return null;
    return (
      <div className="client-notes__news">
        <b className="client-notes__ticker-title">Notes du client</b>
        {notes.length === 0 ? (
          <div className="client-notes__ticker">
            <span className="client-notes__ticker-track client-notes__ticker-track--empty">
              <span className="client-notes__ticker-item">
                <span className="client-notes__ticker-text">
                  Aucune note pour ce client
                </span>
              </span>
            </span>
          </div>
        ) : (
          <div className="client-notes__ticker">
            <div
              className="client-notes__ticker-track"
              style={{ animationDuration: `${notes.length * 12}s` }}
            >
              {[...notes, ...notes].map((note, index) => (
                <div
                  key={`${note.id}-${index}`}
                  className="client-notes__ticker-cell"
                >
                  {renderTickerItem(note)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const list = (
    <div
      className={
        "client-notes__list" +
        (variant === "news" ? " client-notes__feed" : "")
      }
    >
      {loading ? (
        <div className="client-notes__empty">
          <Spin />
        </div>
      ) : notes.length === 0 ? (
        <div className="client-notes__empty">
          <Empty
            description="Aucune note"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        notes.map((note) => renderNote(note))
      )}
    </div>
  );

  if (!canAdd) return <div className="client-notes">{list}</div>;

  return (
    <div className="client-notes">
      <div className="client-notes__form">
        <TextArea
          rows={2}
          placeholder="Ecrivez votre note..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <div className="client-notes__form-row">
          <Select
            value={type}
            onChange={setType}
            style={{ width: 160 }}
            size="small"
          >
            {Object.keys(NOTE_TYPES).map((key) => (
              <Option key={key} value={key}>
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: NOTE_TYPES[key].color,
                    marginRight: 8,
                  }}
                />
                {NOTE_TYPES[key].label}
              </Option>
            ))}
          </Select>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onImageChange}
          />
          <Button size="small" icon={<PictureOutlined />} onClick={pickImage}>
            Image
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            loading={saving}
            onClick={submitNote}
          >
            Ajouter
          </Button>
        </div>
        {imageName && (
          <div className="client-notes__file">
            {imageName}
            <Button
              type="text"
              size="small"
              onClick={() => {
                setImage(null);
                setImageName(null);
              }}
            >
              Retirer
            </Button>
          </div>
        )}
      </div>
      {list}
    </div>
  );
};

export default ClientNotes;
